import React from 'react';
import { Modal, Button, Form, Row, Col, Container } from 'react-bootstrap';
import { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { downloadHistory } from '../../../services/CommonService';
import { imgURL } from '../../Config';
import './PublicationModal.css';




export default function BannerModal({ show, onClose, data }) {
  const getUserId = (userData, visited = new Set()) => {
    if (!userData) return null;
    if (typeof userData === 'object' && visited.has(userData)) return null;
    if (typeof userData === 'object') visited.add(userData);

    if (Array.isArray(userData)) {
      return userData.reduce((id, item) => id || getUserId(item), null);
    }

    if (typeof userData !== 'object') {
      if (typeof userData === 'string' && /^[\[{]/.test(userData.trim())) {
        try {
          return getUserId(JSON.parse(userData), visited);
        } catch (error) {
          return null;
        }
      }
      const numericUserId = Number(userData);
      return Number.isInteger(numericUserId) && numericUserId > 0 ? numericUserId : null;
    }

    const userIdKeys = ['id', 'user_id', 'userId', 'id_user', 'id_mstuser', 'userid', 'userID', 'id_mst_user'];
    for (const key of userIdKeys) {
      const numericUserId = Number(userData[key]);
      if (Number.isInteger(numericUserId) && numericUserId > 0) return numericUserId;
    }

    return Object.keys(userData).reduce((id, key) => id || getUserId(userData[key], visited), null);
  };

  const getStoredUserId = (fallbackId) => {
    const storageKeys = ['usercre', 'user', 'userData', 'user_id', 'userId', 'userID', 'id_mstuser'];
    const storedValues = storageKeys.flatMap((key) => [
      localStorage.getItem(key),
      sessionStorage.getItem(key),
    ]);
    return storedValues.reduce((id, value) => id || getUserId(value), null) || getUserId({ id: fallbackId });
  };

  const initialFormState = {
    name: '',
    phone: '',
    email: '',
    reason_for_download: '',
  };
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    reason_for_download: '',
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [resonData, setReasonData] = useState([]);
  const [modalCheckboxChecked, setModalCheckboxChecked] = useState(false);

  // Show Modal
  useEffect(() => {
    if (show) {
      setFormData(initialFormState); // Reset form
      const resonDa = JSON.parse(localStorage.getItem('lookup'));
      const getUserData = JSON.parse(localStorage.getItem('usercre'));
      setReasonData(resonDa || []);
      if (getUserData) {
        setFormData((prevData) => ({
          ...prevData,
          name: getUserData.name || '',
          email: getUserData.email || '',
          phone: getUserData.phone || '',
          id: getUserId(getUserData)
        }));
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          reason_for_download: '',
        });
      }
    }
  }, [show, data]);

  const handleLinkClick = (e) => {
    e.preventDefault(); // Prevent normal link behavior
    setShowModal(true); // Show modal
  };

  const handleAgree = () => {
    setTermsAccepted(true);
    setShowModal(false);
  };


  // form change control
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      if (!/^\d{0,10}$/.test(value)) return;
      if (value.length > 0 && !/^[6-9]/.test(value)) return;
    }

    if (name === 'name') {
      if (!/^[A-Za-z\s]{0,30}$/.test(value)) return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Close Modal
  const handleClose = () => {
    setFormData(initialFormState);
    onClose(); // Close modal
    setShowModal(false);
  };


  // Submit Upload Form
  const normalizeDownloadUrl = (value, linkMode, defaultFolder = 'files') => {
    if (!value) return null;
    if (typeof value === 'object') {
      if (value.url) return normalizeDownloadUrl(value.url, linkMode, defaultFolder);
      if (value.path) return normalizeDownloadUrl(value.path, linkMode, defaultFolder);
      return null;
    }
    const url = String(value).trim();
    if (!url) return null;

    if (String(linkMode) === '2') return url;
    if (/^https?:\/\//i.test(url) || /^blob:/i.test(url) || /^data:/i.test(url)) return url;
    if (url.startsWith('/')) return `${window.location.origin}${url}`;

    const baseUrl = String(imgURL || '').replace(/\/$/, '');
    const cleaned = url.replace(/^\.\//, '').replace(/^\/+/, '').replace(/^storage\//, '');
    if (/\/publications\/files$/i.test(baseUrl)) {
      return `${baseUrl}/${cleaned.replace(/^publications\/files\//i, '').replace(/^files\//i, '')}`;
    }
    const storagePath = cleaned.startsWith('publications/')
      ? cleaned
      : cleaned.startsWith('thumbnails/')
        ? `publications/${cleaned}`
        : cleaned.startsWith('files/')
          ? `publications/${cleaned}`
          : `publications/${defaultFolder}/${cleaned}`;

    return baseUrl ? `${baseUrl}/${storagePath}` : `${window.location.origin}/${storagePath}`;
  };

  const downloadPublicationFile = async (fileValue, linkMode) => {
    const url = normalizeDownloadUrl(fileValue, linkMode);
    if (!url) {
      Swal.fire({
        title: 'No file available',
        text: 'This publication does not have a downloadable file.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      return;
    }

    const assetUrl = new URL(url, window.location.origin);

    let response;
    try {
      response = await fetch(assetUrl.href);
    } catch (error) {
      throw new Error('Unable to download the file. The file server may be unavailable or may not allow downloads from this site.');
    }
    if (!response.ok) {
      throw new Error(`Unable to download the file (HTTP ${response.status}).`);
    }

    const blobUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = assetUrl.pathname.split('/').pop() || 'publication-file';
    link.style.display = 'none';
    try {
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isDownloading) return;

    const { name, phone, email, reason_for_download, id } = formData;

    if (!name || !phone || !email || !reason_for_download) {
      alert("Please fill in all the fields.");
      return;
    }
    if (!termsAccepted) {
      alert("Please accept the terms and conditions.");
      return;
    }

    const userId = getStoredUserId(id);
    setIsDownloading(true);

    try {
      if (data) {
        // Guest downloads do not require a user-linked history record.
        if (userId) {
          const publicationform = new FormData();
          publicationform.append('user_id', String(userId));
          publicationform.append('publications_id', data.data.id);
          publicationform.append('created_by', '');
          Object.keys(formData).forEach((key) => {
            if (formData[key] !== null) {
              publicationform.append(key, formData[key]);
            }
          });
          await downloadHistory(publicationform);
        }

        await downloadPublicationFile(data.data.file, data.data.link);
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsDownloading(false);
    }

  };

  // All View Here
  return (
    <>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#FFA500", color: "#000", fontWeight: "bold" }}
        >
          {/* <p>{{data}}</p> */}
          <Modal.Title>{data?.data?.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{}}>
          <Container>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="formName">
                    <Form.Label style={{
                      fontSize: 'medium',
                      fontWeight: '600'
                    }}>Name<span class="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Enter name"
                      value={formData.name}
                      onChange={handleChange}
                      style={{
                        border: '1px solid #ed9003',
                        color: '#000',
                        padding: '5px',
                        fontSize: 'smaller'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formPhone">
                    <Form.Label style={{
                      fontSize: 'medium',
                      fontWeight: '600'
                    }}>Phone<span class="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      placeholder="Enter phone"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        border: '1px solid #ed9003',
                        color: '#000',
                        padding: '5px',
                        fontSize: 'smaller'
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="formEmail">
                    <Form.Label style={{
                      fontSize: 'medium',
                      fontWeight: '600'
                    }}>Email<span class="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        border: '1px solid #ed9003',
                        color: '#000',
                        padding: '5px',
                        fontSize: 'smaller'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formReason" className="mb-4">
                    <Form.Label style={{
                      fontSize: 'medium',
                      fontWeight: '600'
                    }}>Reason for download<span class="text-danger">*</span></Form.Label>

                    <Form.Control
                      as="select"
                      name="reason_for_download"
                      value={formData.reason_for_download}
                      onChange={handleChange}
                      style={{
                        border: '1px solid #ed9003',
                        color: '#000',
                        padding: '5px',
                        fontSize: 'smaller'
                      }}
                    >
                      <option value="">Select Reason</option>
                      {resonData.map((typo) => (
                        typo.Flag == 5 ? (
                          <option key={typo.id_mstlookup} value={typo.id_mstlookup}>
                            {typo.LookupValue}
                          </option>
                        ) : null
                      ))}
                    </Form.Control>
                  </Form.Group>

                </Col>
              </Row>
              <Form.Group controlId="formTerms" className={`mt-3 pub-terms${termsAccepted ? ' is-accepted' : ''}`}>
                <Form.Check
                  type="checkbox"
                  label={
                    <>
                      <a href="#" onClick={handleLinkClick}>
                        Click to read and acknowledge the terms & Conditions.
                      </a>
                      <span className="text-danger">*</span>
                    </>
                  }
                  checked={termsAccepted}
                  onChange={() => {
                    if (!termsAccepted) setShowModal(true);
                    else setTermsAccepted(false); // Uncheck directly
                  }}
                />
              </Form.Group>
              <div className="text-end mt-3">
                <Button variant="primary" type="submit" disabled={
                  isDownloading ||
                  !termsAccepted ||
                  !formData.name.trim() ||
                  !formData.phone.trim() ||
                  !formData.email.trim() ||
                  !formData.reason_for_download.trim()
                }>
                  {isDownloading ? 'Downloading...' : 'Download'}
                </Button>{" "}
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Container>
        </Modal.Body>
      </Modal>
      {/* <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton style={{ backgroundColor: "#FFA500", color: "#000", fontWeight: "bold" }}>
          <Modal.Title>Terms and Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Here you can show the full terms and conditions text.</p>
          <p>You can make this scrollable or styled as needed.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAgree}>
            I Agree
          </Button>
        </Modal.Footer>
      </Modal> */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '1000px',
              textAlign: 'center',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
            }}
          >
            <h2>Terms & Conditions</h2>
            <div style={{ maxHeight: '300px', overflowY: 'auto', textAlign: 'left', marginTop: '15px' }}>
              <p><strong>By proceeding to download this report, I hereby agree to the following terms and conditions:</strong></p>

              <ol>
                <li>
                  <strong>Proper Usage:</strong><br />
                  I understand that the report I am downloading is intended for educational, informational, or non-commercial purposes only (unless explicitly stated otherwise). I agree to use the content responsibly and in accordance with its intended use.
                </li>
                <li>
                  <strong>Respect for Intellectual Property:</strong><br />
                  I acknowledge that all intellectual property rights of the content remain with the original creators or the organization providing the report. I will not reproduce, modify, or distribute the report or any part of it without proper attribution or prior written permission.
                </li>
                <li>
                  <strong>License Terms:</strong><br />
                  I agree to honor and comply with all the terms and conditions specified under the report’s license (e.g., Creative Commons, internal organizational policy, etc.).
                </li>
                <li>
                  <strong>Attribution:</strong><br />
                  I will give appropriate credit to the authors or the organization, provide a link to the license (if applicable), and indicate if any changes were made, as per the guidelines.
                </li>
                <li>
                  <strong>No Misuse:</strong><br />
                  I will not use the report or its content for unlawful, misleading, defamatory, or harmful purposes. I will not use the information in any way that could damage the reputation or credibility of the organization or its partners.
                </li>
                <li>
                  <strong>Confidentiality (if applicable):</strong><br />
                  If the report contains any sensitive or restricted information, I agree to maintain its confidentiality and not share it with unauthorized individuals.
                </li>
                <li>
                  <strong>Acknowledgement:</strong><br />
                  By downloading the report, I confirm that I have read, understood, and agreed to the above terms.
                </li>
              </ol>
            </div>

            {/* Checkbox inside Modal */}
            <div className={`pub-terms pub-terms--agree${modalCheckboxChecked ? ' is-accepted' : ''}`}>
              <label className="pub-terms__agree">
                <input
                  type="checkbox"
                  checked={modalCheckboxChecked}
                  onChange={(e) => setModalCheckboxChecked(e.target.checked)}
                />
                I have read and agree to the Terms & Conditions.
              </label>
            </div>

            {/* Modal Buttons */}
            <div style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowModal(false);
                  setModalCheckboxChecked(false); // Reset for next time
                }}
                disabled={!modalCheckboxChecked}
                style={{
                  padding: '10px 20px',
                  backgroundColor: modalCheckboxChecked ? '#007bff' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: modalCheckboxChecked ? 'pointer' : 'not-allowed',
                  marginRight: '10px'
                }}
              >
                Accept
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setModalCheckboxChecked(false); // Optional reset
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
